/**
 * 协同鼠标彩蛋：两个（或更多）鼠标碰到一起时放点小动静。
 *
 * 纯本地判定，不发任何协议消息：每个人拿自己屏幕上看到的位置（自己的真鼠标 + 别人画出来的鼠标）各算各的，
 * 鼠标本来就每 200ms 同步一次，两边看到的是同一下碰撞，前后差不到半秒。
 * 关了「显示别人的鼠标」就不判定——看不见别人的鼠标，也就没有碰撞。
 *
 * 这里只管「什么时候算碰到、碰到算哪种」，坐标一律是屏幕像素；怎么画在 components/version/CursorParty.tsx。
 */

export interface PartyPoint {
  id: string;
  x: number;
  y: number;
  /** 空闲的人：碰他算「戳醒」，不击掌、不冒心 */
  idle?: boolean;
}

export type PartyEvent =
  /** 两个鼠标碰上了。combo：同一对人连着碰的第几下；hard：撞得很快 */
  | { kind: "bump"; a: string; b: string; x: number; y: number; combo: number; hard: boolean }
  /** 碰到一个空闲的人 */
  | { kind: "poke"; who: string; by: string; x: number; y: number }
  /** 两个鼠标贴着不走：隔一会儿冒一颗心 */
  | { kind: "heart"; a: string; b: string; x: number; y: number }
  /** 三个及以上凑到了一块 */
  | { kind: "party"; ids: string[]; x: number; y: number };

export const PARTY = {
  /** 小于这个距离算碰上 */
  touchPx: 22,
  /** 碰上之后要分开到这么远才算分开，下一次才会再响（远程鼠标 200ms 一跳，没有这道余量会抖着连响） */
  releasePx: 56,
  /** 分开后这么久内又碰上，算连击 */
  comboMs: 3000,
  /** 相对速度超过这个（像素/秒）算猛撞 */
  hardSpeed: 1400,
  /** 贴着多久冒第一颗心，之后每隔多久再冒一颗 */
  heartAfterMs: 1200,
  heartEveryMs: 1700,
  /** 三人以上：彼此连得上（相邻两人在这个距离内）就算凑到一块 */
  groupPx: 48,
  /** 凑一块的庆祝多久最多一次 */
  partyCooldownMs: 5000,
  /** 两帧隔太久（页面卡了、切回来）不做扫掠，免得把中间的空白当成一路划过去 */
  sweepMaxMs: 250,
} as const;

interface PairState { touching: boolean; since: number; lastBump: number; combo: number; lastHeart: number }
interface Track { x: number; y: number; t: number; vx: number; vy: number }

export interface PartyState {
  pairs: Map<string, PairState>;
  tracks: Map<string, Track>;
  /** 上一帧凑在一块的组（成员 id 排序后拼起来） */
  groups: Set<string>;
  lastParty: number;
}

export function newPartyState(): PartyState {
  return { pairs: new Map(), tracks: new Map(), groups: new Set(), lastParty: -Infinity };
}

const pairKey = (a: string, b: string) => (a < b ? `${a}\n${b}` : `${b}\n${a}`);

/**
 * 两点各自从上一帧直线走到这一帧，途中最近的距离与那一刻的中点。
 * 快速划过对方时，两帧的位置可能都离得很远、中间却穿过去了——只比当前位置会漏掉
 */
export function closestApproach(a0: { x: number; y: number }, a1: { x: number; y: number }, b0: { x: number; y: number }, b1: { x: number; y: number }) {
  const rx = b0.x - a0.x, ry = b0.y - a0.y;
  const dx = (b1.x - a1.x) - rx, dy = (b1.y - a1.y) - ry;
  const dd = dx * dx + dy * dy;
  const t = dd > 0 ? Math.min(1, Math.max(0, -(rx * dx + ry * dy) / dd)) : 1;
  const ax = a0.x + (a1.x - a0.x) * t, ay = a0.y + (a1.y - a0.y) * t;
  const bx = b0.x + (b1.x - b0.x) * t, by = b0.y + (b1.y - b0.y) * t;
  return { dist: Math.hypot(bx - ax, by - ay), x: (ax + bx) / 2, y: (ay + by) / 2 };
}

/** 连通分量：相邻两人在 px 之内就连起来 */
function clusters(points: PartyPoint[], px: number): PartyPoint[][] {
  const seen = new Set<string>(), out: PartyPoint[][] = [];
  for (const p of points) {
    if (seen.has(p.id)) continue;
    const group: PartyPoint[] = [p];
    seen.add(p.id);
    for (let i = 0; i < group.length; i++) {
      for (const q of points) {
        if (seen.has(q.id) || Math.hypot(q.x - group[i].x, q.y - group[i].y) >= px) continue;
        seen.add(q.id);
        group.push(q);
      }
    }
    out.push(group);
  }
  return out;
}

/**
 * 走一帧：给这一刻屏幕上所有鼠标的位置，返回这一帧该放的动静。state 原地更新。
 * 同一帧里一组人凑到一块就只放一次「聚会」，不再把组里每一对的击掌都放一遍
 */
export function stepParty(state: PartyState, points: PartyPoint[], now: number): PartyEvent[] {
  const events: PartyEvent[] = [];
  const ids = new Set(points.map((p) => p.id));
  for (const id of state.tracks.keys()) if (!ids.has(id)) state.tracks.delete(id);
  for (const k of state.pairs.keys()) {
    const [a, b] = k.split("\n");
    if (!ids.has(a) || !ids.has(b)) state.pairs.delete(k);
  }

  // 速度做个平滑：本地鼠标每帧都在动，远程鼠标是 200ms 的补间，直接拿两帧差太抖
  const prev = new Map<string, Track>();
  for (const p of points) {
    const old = state.tracks.get(p.id);
    if (old) prev.set(p.id, old);
    const dt = old ? (now - old.t) / 1000 : 0;
    const fresh = old && dt > 0 && now - old.t <= PARTY.sweepMaxMs;
    const vx = fresh ? old.vx + ((p.x - old.x) / dt - old.vx) * 0.35 : 0;
    const vy = fresh ? old.vy + ((p.y - old.y) / dt - old.vy) * 0.35 : 0;
    state.tracks.set(p.id, { x: p.x, y: p.y, t: now, vx, vy });
  }

  // 三人以上凑一块
  const groups = new Set<string>();
  /** 人 → 他此刻所在的那一组：同组的人之间不再单独击掌、冒心，整组只放一次聚会 */
  const groupOf = new Map<string, string>();
  for (const g of clusters(points.filter((p) => !p.idle), PARTY.groupPx)) {
    if (g.length < 3) continue;
    const key = g.map((p) => p.id).sort().join("\n");
    groups.add(key);
    for (const p of g) groupOf.set(p.id, key);
    if (state.groups.has(key) || now - state.lastParty < PARTY.partyCooldownMs) continue;
    state.lastParty = now;
    events.push({ kind: "party", ids: g.map((p) => p.id), x: g.reduce((s, p) => s + p.x, 0) / g.length, y: g.reduce((s, p) => s + p.y, 0) / g.length });
  }
  state.groups = groups;

  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const a = points[i], b = points[j];
      const key = pairKey(a.id, b.id);
      const ps = state.pairs.get(key) ?? { touching: false, since: 0, lastBump: -Infinity, combo: 0, lastHeart: 0 };
      state.pairs.set(key, ps);
      const pa = prev.get(a.id), pb = prev.get(b.id);
      const sweep = pa && pb && now - pa.t <= PARTY.sweepMaxMs && now - pb.t <= PARTY.sweepMaxMs;
      const near = sweep ? closestApproach(pa, a, pb, b) : { dist: Math.hypot(b.x - a.x, b.y - a.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      const sameGroup = groupOf.has(a.id) && groupOf.get(a.id) === groupOf.get(b.id);

      if (ps.touching) {
        if (dist > PARTY.releasePx) { ps.touching = false; continue; }
        // 贴着不走：冒心（空闲的人不算，他没在跟你玩）
        if (!sameGroup && !a.idle && !b.idle && now - ps.since >= PARTY.heartAfterMs && now - ps.lastHeart >= PARTY.heartEveryMs) {
          ps.lastHeart = now;
          events.push({ kind: "heart", a: a.id, b: b.id, x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
        }
        continue;
      }
      if (near.dist >= PARTY.touchPx) continue;

      ps.touching = true;
      ps.since = now;
      // 第一颗心从碰上之后 heartAfterMs 起算
      ps.lastHeart = now - PARTY.heartEveryMs + PARTY.heartAfterMs;
      if (sameGroup) continue;
      if (a.idle || b.idle) {
        if (a.idle && b.idle) continue;
        const [who, by] = a.idle ? [a, b] : [b, a];
        events.push({ kind: "poke", who: who.id, by: by.id, x: who.x, y: who.y });
        continue;
      }
      ps.combo = now - ps.lastBump <= PARTY.comboMs ? ps.combo + 1 : 1;
      ps.lastBump = now;
      const ta = state.tracks.get(a.id)!, tb = state.tracks.get(b.id)!;
      const hard = Math.hypot(ta.vx - tb.vx, ta.vy - tb.vy) >= PARTY.hardSpeed;
      events.push({ kind: "bump", a: a.id, b: b.id, x: near.x, y: near.y, combo: ps.combo, hard });
    }
  }
  return events;
}
